import { format } from "date-fns";
import { da, enUS } from "date-fns/locale";
import {
  NOTIFICATION_DATE_FORMAT,
  NOTIFICATION_DATE_MODE,
  USE_LOCALIZED_MONTH_NAMES,
} from "../constants";

const LOCALES = { da, en: enUS };

function resolveLocale(language) {
  if (!USE_LOCALIZED_MONTH_NAMES) return LOCALES.en;
  return String(language).toLowerCase().startsWith("da") ? LOCALES.da : LOCALES.en;
}

function toValidDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function isSameDay(a, b) {
  return (
    a.getDate() === b.getDate() &&
    a.getMonth() === b.getMonth() &&
    a.getFullYear() === b.getFullYear()
  );
}

function startOfPreviousDay(now) {
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  return yesterday;
}

export function formatAbsoluteNotificationDate(createdAt, language = "en") {
  const created = toValidDate(createdAt);
  if (!created) return "";

  return format(created, NOTIFICATION_DATE_FORMAT, { locale: resolveLocale(language) });
}

function formatTimeAgo(created, now, translate) {
  const diffSeconds = Math.max(0, Math.floor((now - created) / 1000));
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);

  if (diffSeconds < 60) {
    return translate("main.notifications.time.secondsAgo", {
      count: diffSeconds,
      defaultValue: `${diffSeconds}s ago`,
    });
  }

  if (diffMinutes < 60) {
    return translate("main.notifications.time.minutesAgo", {
      count: diffMinutes,
      defaultValue: `${diffMinutes}m ago`,
    });
  }

  return translate("main.notifications.time.hoursAgo", {
    count: diffHours,
    defaultValue: `${diffHours}h ago`,
  });
}

export function formatNotificationTime(
  createdAt,
  { t, language = "en", mode = NOTIFICATION_DATE_MODE.RELATIVE, now = new Date() } = {},
) {
  const created = toValidDate(createdAt);
  if (!created) return "";

  if (mode === NOTIFICATION_DATE_MODE.ABSOLUTE) {
    return formatAbsoluteNotificationDate(created, language);
  }

  const translate = typeof t === "function" ? t : (_key, options) => options?.defaultValue ?? "";

  if (isSameDay(created, now)) {
    return formatTimeAgo(created, now, translate);
  }

  if (isSameDay(created, startOfPreviousDay(now))) {
    return translate("main.notifications.time.yesterday", { defaultValue: "Yesterday" });
  }

  return formatAbsoluteNotificationDate(created, language);
}
